import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, effect, inject, input, numberAttribute, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArchive,
  lucideArrowRight,
  lucideBadgeCheck,
  lucideDownload,
  lucideExternalLink,
  lucideGithub,
  lucideGlobe,
  lucidePocketKnife,
  lucideRefreshCw,
  lucideSearch,
  lucideStar,
  lucideTriangleAlert,
  lucideUsers,
  lucideX,
} from '@ng-icons/lucide';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmBadgeDirective } from '@spartan-ng/ui-badge-helm';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmIconDirective } from '@spartan-ng/ui-icon-helm';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import { HlmLabelDirective } from '@spartan-ng/ui-label-helm';
import { HlmNumberedPaginationComponent } from '@spartan-ng/ui-pagination-helm';
import { HlmSelectImports } from '@spartan-ng/ui-select-helm';
import { HlmTooltipImports } from '@spartan-ng/ui-tooltip-helm';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorMessageComponent } from '../../components/error-message/error-message.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { McpServer } from '../../models/mcp.model';
import { HighlightSearchPipe } from '../../pipes/highlight-search.pipe';
import { McpService } from '../../services/mcp.service';
import { SEOService } from '../../services/seo.service';
import { StructuredDataService } from '../../services/structured-data.service';
import { ItemListSchema, ListItemSchema, SoftwareSourceCodeSchema } from '../../types/structured-data.interfaces'; // Updated import path
import { formatNumberShorthand } from '../../utils/number-formatting.utils';

@Component({
  selector: 'app-mcp-servers',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HlmButtonDirective,
    LoadingSpinnerComponent,
    ErrorMessageComponent,
    NgIcon,
    HlmNumberedPaginationComponent,
    HlmInputDirective,
    HlmLabelDirective,
    HlmSelectImports,
    BrnSelectImports,
    HlmBadgeDirective,
    HlmTooltipImports,
    HlmIconDirective,
    HighlightSearchPipe,
  ],
  templateUrl: './mcp-servers.component.html',
  styleUrl: './mcp-servers.component.scss',
  providers: [
    provideIcons({
      lucideTriangleAlert,
      lucideRefreshCw,
      lucideStar,
      lucideDownload,
      lucideGithub,
      lucideSearch,
      lucideArrowRight,
      lucideBadgeCheck,
      lucideGlobe,
      lucideExternalLink,
      lucideUsers,
      lucidePocketKnife,
      lucideArchive,
      lucideX,
    }),
  ],
})
export class McpServersComponent {
  private mcpService = inject(McpService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private seoService = inject(SEOService);
  private structuredDataService = inject(StructuredDataService);
  private breakpointObserver = inject(BreakpointObserver);
  private document = inject(DOCUMENT);

  page = input(1, { transform: (value: unknown) => numberAttribute(value, 1) });
  pageSize = input(12, { transform: (value: unknown) => numberAttribute(value, 12) });
  sort = input<'stars' | 'downloads' | 'alphabetical'>('stars');
  search = input<string>(''); // Input from URL
  serverType = input<'all' | 'official' | 'archived' | 'community'>('all'); // New input for server type filter

  // Initialize FormControls with values from the URL inputs
  searchInputControl = new FormControl(this.search(), { nonNullable: true });
  sortControl = new FormControl<'stars' | 'downloads' | 'alphabetical'>(this.sort(), { nonNullable: true });
  typeFilter = signal<'all' | 'official' | 'archived' | 'community'>('all'); // Signal to track active type filter

  servers = signal<McpServer[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  totalItems = signal(0);

  responsiveMaxSize = signal(7); // Default maxSize

  sortOptions = [
    { value: 'stars', label: 'Stars (high to low)' },
    { value: 'downloads', label: 'Downloads (high to low)' },
    { value: 'alphabetical', label: 'Name (A-Z)' },
  ];

  protected readonly formatNumber = formatNumberShorthand;

  constructor() {
    const currentParams = this.activatedRoute.snapshot.queryParams;
    const paramsToSet: Record<string, string | number> = {};
    let performNavigation = false;

    // Normalize 'sort' parameter
    const expectedSort = this.sort(); // Default 'stars' if QP is missing
    if (!currentParams['sort']) {
      paramsToSet['sort'] = expectedSort;
      performNavigation = true;
    }

    // Normalize 'search' parameter
    const expectedSearch = this.search(); // Default '' if QP is missing
    // Add search to QPs if it's completely missing (undefined)
    if (typeof currentParams['search'] === 'undefined') {
      paramsToSet['search'] = expectedSearch;
      performNavigation = true;
    }

    // Normalize 'page' parameter
    const expectedPage = this.page(); // Default 1 if QP is missing
    if (!currentParams['page']) {
      paramsToSet['page'] = expectedPage;
      performNavigation = true;
    }

    // Normalize 'pageSize' parameter
    const expectedPageSize = this.pageSize(); // Default 12 if QP is missing
    if (!currentParams['pageSize']) {
      paramsToSet['pageSize'] = expectedPageSize;
      performNavigation = true;
    }

    // Normalize 'serverType' parameter
    const expectedServerType = this.serverType(); // Default 'all' if QP is missing
    if (!currentParams['serverType']) {
      paramsToSet['serverType'] = expectedServerType;
      performNavigation = true;
    }

    if (performNavigation) {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: paramsToSet,
        queryParamsHandling: 'merge',
        replaceUrl: true, // Avoid adding to browser history for this normalization
      });
      // The component inputs will update from the new URL, and the effect will run.
    }

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(takeUntilDestroyed())
      .subscribe(result => {
        if (result.breakpoints[Breakpoints.XSmall]) {
          this.responsiveMaxSize.set(3);
        } else if (result.breakpoints[Breakpoints.Small]) {
          this.responsiveMaxSize.set(5);
        } else {
          this.responsiveMaxSize.set(7); // Default for larger screens
        }
      });

    // Effect to react to changes in page, pageSize, sort, or search inputs (from URL)
    effect(() => {
      const currentPage = this.page();
      const currentPageSize = this.pageSize();
      const currentSortFromInput = this.sort();
      let currentSearchTermFromInput = this.search(); // Value from URL
      const currentServerType = this.serverType(); // Value from URL

      if (typeof currentSearchTermFromInput === 'undefined') {
        currentSearchTermFromInput = '';
      }

      // Sync searchInputControl with URL's search param
      if (this.searchInputControl.value !== currentSearchTermFromInput) {
        this.searchInputControl.setValue(currentSearchTermFromInput, { emitEvent: false });
      }

      // Sync sortControl with URL's sort param
      if (this.sortControl.value !== currentSortFromInput) {
        this.sortControl.setValue(currentSortFromInput, { emitEvent: false });
      }

      // Sync typeFilter with URL's serverType param
      this.typeFilter.set(currentServerType);

      this.loadServers(
        currentPage,
        currentPageSize,
        currentSortFromInput,
        currentSearchTermFromInput,
        currentServerType
      );
    });

    this.setSeoAndStructuredData(); // Call method to set SEO and structured data

    // Subscribe to searchInputControl value changes for debouncing
    this.searchInputControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(debouncedSearchValue => {
        // Only perform search if more than 1 character or empty string (to clear search)
        if (debouncedSearchValue.length > 1 || debouncedSearchValue.length === 0) {
          // Check against the current URL state (via this.search() signal)
          if (debouncedSearchValue !== this.search()) {
            this.router.navigate([], {
              relativeTo: this.activatedRoute,
              queryParams: { search: debouncedSearchValue, page: 1 }, // Reset to page 1
              queryParamsHandling: 'merge',
            });
          }
        }
      });

    // Subscribe to sortControl value changes
    this.sortControl.valueChanges.pipe(distinctUntilChanged()).subscribe(newSortValue => {
      // Check against the current URL state (via this.sort() signal)
      if (newSortValue !== this.sort()) {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { sort: newSortValue, page: 1 }, // Reset to page 1
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  onPageChange(newPage: number) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: newPage },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onPageSizeChange(newPageSize: number) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { page: 1, pageSize: newPageSize }, // Reset to page 1
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onSortChange(newSort: 'stars' | 'downloads' | 'alphabetical') {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { sort: newSort, page: 1 }, // Reset to page 1
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  onTypeFilterChange(newType: 'all' | 'official' | 'archived' | 'community') {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { serverType: newType, page: 1 }, // Reset to page 1
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  clearSearch() {
    this.searchInputControl.setValue('');
  }

  loadServers(
    page: number,
    pageSize: number,
    sort: 'stars' | 'downloads' | 'alphabetical',
    search?: string,
    serverType?: 'all' | 'official' | 'archived' | 'community'
  ) {
    console.log('[McpServersComponent] loadServers called with search:', search, 'serverType:', serverType);
    this.loading.set(true);
    this.error.set(null);
    const effectivePage = Math.max(1, page);

    this.mcpService.getMcpServers(effectivePage, pageSize, sort, search, serverType).subscribe({
      next: data => {
        this.servers.set(data.items);
        this.totalItems.set(data.total);
        this.loading.set(false);
        this.setSeoAndStructuredData();
      },
      error: err => {
        this.error.set('Failed to load MCP servers. Please try again later.');
        this.loading.set(false);
        console.error('Error loading MCP servers:', err);
      },
    });
  }

  // Method to set SEO and structured data
  private setSeoAndStructuredData() {
    const canonicalUrl = `${environment.canonicalOrigin}/mcp-servers`;
    const pageTitle = 'MCP Servers | Summerry';
    const pageDescription =
      'Explore a comprehensive list of Model Context Protocol (MCP) servers. Find and connect to servers providing diverse AI models and capabilities.';

    this.seoService.updateTags({
      title: pageTitle,
      description: pageDescription,
      keywords: 'MCP servers, Model Context Protocol, AI models, AI capabilities, server list, AI infrastructure',
      type: 'website',
      url: canonicalUrl,
    });

    this.structuredDataService.addBaseNodes();
    this.structuredDataService.addBreadcrumb([{ name: 'Home', item: origin }, { name: 'MCP Servers' }]);

    const serverItems = this.servers();
    const authorNodes: any = {}; // To store unique author Organization nodes

    // 1. Create Author Organization nodes
    serverItems.forEach(server => {
      const serverRepoUrl = server.url || server.externalUrl;
      if (serverRepoUrl) {
        const repoOwner = this.getRepoOwner(serverRepoUrl);
        if (repoOwner && !authorNodes[repoOwner]) {
          const authorRepositoryUrl = `https://github.com/${repoOwner}`;
          authorNodes[repoOwner] = {
            '@type': 'Organization',
            '@id': authorRepositoryUrl,
            name: repoOwner,
            url: authorRepositoryUrl,
          };
          this.structuredDataService.addNodes(authorNodes[repoOwner]);
        }
      }
    });

    // 2. Create ItemListElements with ListItem wrappers and correct author references
    const itemListElements: ListItemSchema[] = serverItems.map((server, index) => {
      const canonicalProductUrl = server.url;
      const softwareId = canonicalProductUrl || `${canonicalProductUrl}/${server.id}`; // Fallback if no canonical URL

      const softwareItem: SoftwareSourceCodeSchema = {
        '@type': 'SoftwareSourceCode',
        '@id': softwareId,
        name: server.name,
        description:
          server.summary || server.description || 'An MCP server compatible with the Model Context Protocol.',
        url: canonicalProductUrl,
        codeRepository: canonicalProductUrl,
      };

      return {
        '@type': 'ListItem',
        position: index + 1,
        item: softwareItem,
      };
    });

    // Refactored to use addCollectionPage
    const itemListMainEntity: ItemListSchema = {
      '@type': 'ItemList',
      name: 'MCP Server List', // Name for the ItemList itself
      itemListElement: itemListElements,
    };

    this.structuredDataService.addCollectionPage({
      name: pageTitle, // This will be the name of the WebPage/CollectionPage
      description: pageDescription, // Description for WebPage/CollectionPage
      mainEntity: itemListMainEntity, // The ItemList node
      searchPath: '/mcp-servers', // Added searchPath
    });

    this.structuredDataService.generateAndInjectSchema();
  }

  getRepoNameFromUrl(url: string): string {
    if (!url) return '';
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.hostname === 'github.com') {
        const pathParts = parsedUrl.pathname.split('/').filter(part => part.length > 0);
        if (pathParts.length >= 2) {
          return `${pathParts[0]}/${pathParts[1]}`;
        }
      }
    } catch {
      // Invalid URL, ignore error and return empty string
      return '';
    }
    return ''; // Return empty if not a github url or format is unexpected
  }

  getRepoOwner(url: string): string {
    if (!url) return '';
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.hostname === 'github.com') {
        const pathParts = parsedUrl.pathname.split('/').filter(part => part.length > 0);
        if (pathParts.length >= 1) {
          return pathParts[0]; // Return only the owner/organization
        }
      }
    } catch {
      // Invalid URL, ignore error and return empty string
      return '';
    }
    return ''; // Return empty if not a github url or format is unexpected
  }
}
